import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import type { ApolloError } from '@apollo/client';
import {
  AllProjectTranslationsQuery,
  ModelledProductIndexQuery,
  ProjectNormSetByIdQuery,
  ProjectTranslationByIdQuery,
  type ProjectNormSetRow,
  type TranslationRow,
} from '@graphql/queries/PerceptionModelQueries';
import { useSelector } from 'react-redux';
import selectWorkspaceProducerId from 'selectors/workspaceProducerId';

/**
 * Everything the translation edit window reads, in dependency order.
 *
 * The parent resolves first and is what scopes the rest: this table carries no
 * producerId, so a translation reaches its workspace only through its norm set.
 * Each query is skipped until the one it depends on has arrived, which is what
 * stops a translation id typed from another workspace from being fetched at
 * all rather than fetched and then hidden.
 */

export type TranslationRowState = {
  normSet: ProjectNormSetRow | null;
  translation: TranslationRow | null;
  /**
   * Product names this job actually modelled.
   *
   * Only used to say which stored products the render would stop on. The job
   * row itself is no longer read: the page named it from the norm set, and the
   * only thing that needed the record was the map.
   */
  modelled: Set<string>;
  /** owning workspace, needed to offer anchor candidates from it */
  producerId: number;
  /**
   * How many translations hang off the parent norm set.
   *
   * Moving the anchors is a write on the norm set, so every sibling reads
   * against the result. That number belongs in front of the analyst before
   * they move them, not after.
   */
  translationCount: number;
  loading: boolean;
  error?: ApolloError;
};

const useTranslationRow = (
  normSetId: number,
  translationId: number,
): TranslationRowState => {
  const producerId = useSelector((state) => selectWorkspaceProducerId(state));

  const normSetQuery = useQuery(ProjectNormSetByIdQuery, {
    variables: { id: normSetId, producerId },
    skip: !producerId,
  });
  const normSet: ProjectNormSetRow | null =
    normSetQuery.data?.allPerceptionModelProjects?.nodes?.[0] ?? null;

  const translationQuery = useQuery(ProjectTranslationByIdQuery, {
    variables: { id: translationId, projectId: normSetId },
    skip: !normSet,
  });
  const translation: TranslationRow | null =
    translationQuery.data?.allPerceptionModelProjectTranslations?.nodes?.[0] ??
    null;

  const jobId = normSet?.perceptionModelJobId;
  const modelledQuery = useQuery(ModelledProductIndexQuery, {
    variables: { jobId },
    skip: !jobId,
  });
  const modelled = useMemo(
    () =>
      new Set<string>(
        (modelledQuery.data?.allPerceptionModelProductLookups?.nodes ?? []).map(
          (n: { productName: string }) => n.productName,
        ),
      ),
    [modelledQuery.data],
  );

  // Shares the cache entry the norm set page already reads, so on the usual
  // path here — click a translation from its parent — this is a count rather
  // than a second round trip.
  const siblingsQuery = useQuery(AllProjectTranslationsQuery, {
    variables: { projectId: normSetId },
    skip: !normSet,
  });

  return {
    normSet,
    translation,
    modelled,
    producerId,
    translationCount:
      siblingsQuery.data?.allPerceptionModelProjectTranslations?.nodes
        ?.length ?? 0,
    loading: normSetQuery.loading || translationQuery.loading || !producerId,
    error: normSetQuery.error || translationQuery.error,
  };
};

export default useTranslationRow;
